/**
 * LedgerInbox — Inbox akisi: eslesmemis transferler + acik faturalar.
 * Tek bir secim akisi: SOL transfer sec -> SAG fatura(lar) sec -> Eslestir.
 * Sade, tek butonlu, yan yana.
 */
import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Sparkles, X, Search, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmt = (v) => (Number(v) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function LedgerInbox({ inbox, onChange, onLocalUpdate }) {
    const [selTx, setSelTx] = useState(new Set());
    const [selInv, setSelInv] = useState(new Set());
    const [matching, setMatching] = useState(false);
    const [autoMatching, setAutoMatching] = useState(false);
    const [forceClose, setForceClose] = useState(false);
    const [txQ, setTxQ] = useState('');
    const [invQ, setInvQ] = useState('');
    const [direction, setDirection] = useState('all'); // all | in | out
    const [invType, setInvType] = useState('all'); // all | sales | purchase | credit

    const txs = inbox?.unmatched_transactions || [];
    const invs = inbox?.open_invoices || [];

    // Filtrelenmis listeler
    const filteredTxs = useMemo(() => {
        return txs.filter(t => {
            if (direction === 'in' && (t.amount || 0) <= 0) return false;
            if (direction === 'out' && (t.amount || 0) >= 0) return false;
            if (!txQ.trim()) return true;
            const q = txQ.toLowerCase();
            const hay = `${t.communication || ''} ${t.reference || ''} ${t.counterparty_name || ''} ${t.amount}`.toLowerCase();
            return hay.includes(q);
        });
    }, [txs, txQ, direction]);

    // Secili transferin ipucularina gore fatura siralamasi
    const selTxObj = selTx.size === 1 ? txs.find(t => selTx.has(t.id)) : null;
    const sortedInvs = useMemo(() => {
        const list = [...invs];
        if (!selTxObj) return list;
        const txAmt = Math.abs(selTxObj.amount || 0);
        const comm = `${selTxObj.communication || ''} ${selTxObj.reference || ''}`.toLowerCase();
        const isIn = (selTxObj.amount || 0) > 0;
        list.forEach(inv => {
            const no = (inv.invoice_number || '').toLowerCase();
            const digits = no.replace(/\D/g, '');
            const numMatch = (no.length >= 4 && comm.includes(no)) ||
                (digits.length >= 4 && comm.includes(digits));
            const diff = Math.abs((inv.remaining || 0) - txAmt);
            const amtExact = diff < 0.5;
            // Yon uyumu: in -> sales/sales_credit ucretsiz, out -> purchase/purchase_credit
            const dirOk = isIn ? (inv.type === 'sales' || inv.type === 'sales_credit')
                : (inv.type === 'purchase' || inv.type === 'purchase_credit');
            inv._priority = numMatch ? 0 : (dirOk && amtExact ? 1 : (dirOk ? 2 : 3));
            inv._numMatch = numMatch;
            inv._amtExact = amtExact;
            inv._dirOk = dirOk;
        });
        list.sort((a, b) => (a._priority - b._priority) || (a.invoice_date || '').localeCompare(b.invoice_date || ''));
        return list;
    }, [invs, selTxObj]);

    const filteredInvs = useMemo(() => {
        let list = sortedInvs;
        if (invType !== 'all') {
            list = list.filter(i => {
                const t = i.type || '';
                if (invType === 'sales') return t === 'sales';
                if (invType === 'purchase') return t === 'purchase';
                if (invType === 'credit') return t.includes('credit');
                return true;
            });
        }
        if (!invQ.trim()) return list;
        const q = invQ.toLowerCase();
        return list.filter(inv => {
            const hay = `${inv.invoice_number || ''} ${inv.contact_name || ''} ${inv.total} ${inv.notes || ''}`.toLowerCase();
            return hay.includes(q);
        });
    }, [sortedInvs, invQ, invType]);

    const toggleSet = (setter) => (id) => setter(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    // Cari hesap perspektifi:
    // - sales / purchase_credit → +pozitif (musteri/biz bize odeme yapacak)
    // - purchase / sales_credit → -negatif (biz odeyecegiz / musteri lehine azalir)
    // Boylece gelen tahsilatla satis VE alis ayni anda eslestirildiginde,
    // alis fatura tutardan DUSULUR (mahsup). Ornek: +1.086,10 = 6.215 (S) - 5.128,90 (A)
    const _typeSign = (t) => {
        if (t === 'purchase' || t === 'sales_credit') return -1;
        return +1;
    };

    const sumSelTx = useMemo(() => Array.from(selTx).reduce((s, id) => {
        const t = txs.find(x => x.id === id);
        // Signed: gelen tahsilat (+), giden odeme (-)
        return s + (t?.amount || 0);
    }, 0), [selTx, txs]);
    const sumSelInv = useMemo(() => Array.from(selInv).reduce((s, id) => {
        const i = invs.find(x => x.id === id);
        if (!i) return s;
        return s + _typeSign(i.type || '') * Math.abs(i.remaining || 0);
    }, 0), [selInv, invs]);
    const diff = Math.abs(sumSelTx - sumSelInv);

    // "Farki kabul et" mantikli olmasi icin: fark, secili fatura(lar) toplamlarinin
    // mutlak degerinin %5'inden veya 50 EUR'dan az olmali. Aksi halde absurt komisyon
    // onerisi cikiyor (orn. 16.745 EUR komisyon teklif edilmesi gibi).
    const reasonableFeeLimit = Math.max(50, Math.abs(sumSelInv) * 0.05);
    const isReasonableDiff = diff <= reasonableFeeLimit;

    // Eski "onlyCreditsSelected" → artik signed sum tx ile karsilastirilarak yon kontrol edilir.
    // tx > 0 (gelen) bekliyoruz sumSelInv > 0 (satis baskinligi); tam tersi giden icin.
    // Asagidaki uyari: secilen faturalar tx yonune zit isaret veriyorsa "eksik secim" der.
    const directionMismatch = selInv.size > 0 && selTx.size > 0 && (
        (sumSelTx > 0 && sumSelInv < 0) || (sumSelTx < 0 && sumSelInv > 0)
    );
    const onlyCreditsSelected = directionMismatch;

    const doMatch = async () => {
        if (selTx.size === 0 || selInv.size === 0) {
            return toast.error('En az 1 transfer ve 1 fatura sec');
        }
        // Guvenlik: absurt fark ile force_close yapilmasin (kullanici checkbox'i unutmus olabilir)
        if (forceClose && !isReasonableDiff) {
            return toast.error(`Fark cok buyuk (${fmt(diff)} EUR) — banka komisyonu olamaz. Once eksik fatura/iadeleri sec.`);
        }
        if (diff > Math.max(0.5, Math.abs(sumSelInv) * 0.01) && !forceClose) {
            const hint = onlyCreditsSelected
                ? '\n\nUYARI: Sadece iade (S-IADE) fatura(lar) secili. Muhtemelen ayni dekontla kapanan satis faturasini da secmen gerekiyor.'
                : !isReasonableDiff
                    ? `\n\nDIKKAT: Fark (${fmt(diff)} EUR) cok yuksek — komisyon olamaz. Eksik fatura olabilir.`
                    : '\n\nIP UCU: "Farki kabul et" kutusunu isaretleyip faturalari tamamen kapatabilirsin (komisyon/kur farki vs).';
            if (!window.confirm(`Tutar uyusmuyor: Transfer ${fmt(sumSelTx)} EUR vs Fatura ${fmt(sumSelInv)} EUR (fark ${fmt(diff)}). Devam et?${hint}`)) return;
        }
        setMatching(true);
        try {
            if (selTx.size === 1) {
                const txId = Array.from(selTx)[0];
                await axios.post(`${API}/api/accounting/reconciliation/match`,
                    { transaction_id: txId, invoice_ids: Array.from(selInv), force_close: forceClose }, { headers: headers() });
                toast.success(forceClose ? 'Eslestirildi · Fatura(lar) tam kapatildi' : 'Eslestirildi');
            } else if (selInv.size === 1) {
                const invId = Array.from(selInv)[0];
                await axios.post(`${API}/api/accounting/reconciliation/match-multi-tx`,
                    { invoice_id: invId, transaction_ids: Array.from(selTx) }, { headers: headers() });
                toast.success(`${selTx.size} transfer faturaya eklendi`);
            } else {
                for (const txId of Array.from(selTx)) {
                    // eslint-disable-next-line no-await-in-loop
                    await axios.post(`${API}/api/accounting/reconciliation/match`,
                        { transaction_id: txId, invoice_ids: Array.from(selInv), force_close: forceClose }, { headers: headers() });
                }
                toast.success(`${selTx.size} transfer ${selInv.size} faturaya dagitildi`);
            }
            setSelTx(new Set()); setSelInv(new Set()); setForceClose(false);
            // Optimistic: matched tx + invoice'lari inbox'tan cikar (instant feedback)
            if (onLocalUpdate) {
                const matchedTxIds = new Set(Array.from(selTx));
                const matchedInvIds = new Set(Array.from(selInv));
                onLocalUpdate(prev => ({
                    ...prev,
                    inbox: {
                        open_invoices: (prev.inbox?.open_invoices || []).filter(i => !matchedInvIds.has(i.id)),
                        unmatched_transactions: (prev.inbox?.unmatched_transactions || []).filter(t => !matchedTxIds.has(t.id)),
                    },
                    counts: {
                        ...prev.counts,
                        inbox_open_invoices: Math.max(0, (prev.counts?.inbox_open_invoices || 0) - matchedInvIds.size),
                        inbox_unmatched_tx: Math.max(0, (prev.counts?.inbox_unmatched_tx || 0) - matchedTxIds.size),
                    },
                }));
            } else {
                onChange?.();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Eslestirme hatasi');
        } finally { setMatching(false); }
    };

    const doAutoMatch = async () => {
        // Guvenli adaylar: tutar tam + fatura no eslesti
        const plans = [];
        const used = new Set();
        for (const tx of txs) {
            const txAmt = Math.abs(tx.amount || 0);
            const comm = `${tx.communication || ''} ${tx.reference || ''}`.toLowerCase();
            const isIn = (tx.amount || 0) > 0;
            const candidates = invs.filter(i => !used.has(i.id) &&
                (isIn ? (i.type === 'sales' || i.type === 'sales_credit')
                    : (i.type === 'purchase' || i.type === 'purchase_credit')));
            const scored = candidates.map(inv => {
                const no = (inv.invoice_number || '').toLowerCase();
                const digits = no.replace(/\D/g, '');
                const numMatch = (no.length >= 4 && comm.includes(no)) ||
                    (digits.length >= 4 && comm.includes(digits));
                const diff = Math.abs((inv.remaining || 0) - txAmt);
                return { inv, numMatch, exact: diff < 0.5 };
            });
            const best = scored.filter(s => s.numMatch && s.exact);
            if (best.length === 1) { plans.push({ tx: tx.id, inv: best[0].inv.id, why: 'no+amount' }); used.add(best[0].inv.id); continue; }
            const onlyNo = scored.filter(s => s.numMatch);
            if (onlyNo.length === 1) { plans.push({ tx: tx.id, inv: onlyNo[0].inv.id, why: 'no' }); used.add(onlyNo[0].inv.id); continue; }
            const onlyExact = scored.filter(s => s.exact);
            if (onlyExact.length === 1) { plans.push({ tx: tx.id, inv: onlyExact[0].inv.id, why: 'amount' }); used.add(onlyExact[0].inv.id); continue; }
        }
        if (plans.length === 0) return toast.info('Otomatik eslesecek guvenli kombinasyon yok');
        if (!window.confirm(`${plans.length} guvenli eslesme bulundu. Onayla ve eslestir?`)) return;
        setAutoMatching(true);
        let ok = 0, fail = 0;
        for (const p of plans) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await axios.post(`${API}/api/accounting/reconciliation/match`,
                    { transaction_id: p.tx, invoice_ids: [p.inv] }, { headers: headers() });
                ok++;
            } catch (e) { fail++; }
        }
        setAutoMatching(false);
        if (fail === 0) toast.success(`${ok} eslesme tamamlandi`);
        else toast.warning(`${ok} basarili, ${fail} basarisiz`);
        // Auto-match cok kayit etkiliyor -> full sync (optimistic karmasik olur)
        onChange?.();
    };

    if (txs.length === 0 && invs.length === 0) {
        return (
            <div className="bg-emerald-50/50 border border-emerald-200 rounded-2xl p-12 text-center" data-testid="ledger-inbox-empty">
                <CheckCircle2 size={36} className="mx-auto text-emerald-500 mb-3" />
                <p className="text-sm font-semibold text-emerald-900">Inbox bos</p>
                <p className="text-xs text-emerald-700 mt-1">Eslesmemis transfer veya acik fatura yok</p>
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="ledger-inbox">
            {/* Hizli mutabakat */}
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-[12px] text-indigo-900">
                    <Sparkles size={14} className="text-indigo-600" />
                    <span><strong>Hizli Mutabakat:</strong> tutar veya fatura no eslesenler tek tikla baglanir.</span>
                </div>
                <button onClick={doAutoMatch} disabled={autoMatching}
                    className="h-8 px-3 bg-indigo-600 hover:bg-indigo-700 text-white text-[12px] font-bold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="ledger-inbox-auto-match-btn">
                    {autoMatching ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    {autoMatching ? 'Eslestiriliyor...' : 'Otomatik Eslestir'}
                </button>
            </div>

            {/* Direction filter */}
            <div className="flex gap-1.5 items-center">
                <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Yon:</span>
                {[['all', 'Tumu'], ['in', 'Gelen +'], ['out', 'Giden -']].map(([k, l]) => (
                    <button key={k} onClick={() => setDirection(k)}
                        className={`px-3 py-1 rounded-lg text-[11px] font-semibold ${direction === k ? 'bg-[#111] text-white' : 'bg-[#F3F4F6] text-[#6B7280]'}`}
                        data-testid={`ledger-inbox-dir-${k}`}>{l}</button>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* SOL: Transferler */}
                <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden">
                    <div className="px-4 py-2.5 bg-[#FAFAFA] border-b border-[#F3F4F6]">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[12px] font-semibold text-[#374151]">Eslesmemis Transferler ({filteredTxs.length})</span>
                            {selTx.size > 0 && (
                                <button onClick={() => setSelTx(new Set())} className="text-[10px] text-red-500 font-medium" data-testid="ledger-inbox-tx-clear">Temizle ({selTx.size})</button>
                            )}
                        </div>
                        <div className="relative">
                            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                            <input value={txQ} onChange={e => setTxQ(e.target.value)}
                                placeholder="Aciklama, ref, karsi taraf ara..."
                                className="w-full h-7 pl-6 pr-2 bg-white border border-[#E5E7EB] rounded text-[11px]"
                                data-testid="ledger-inbox-tx-search" />
                        </div>
                    </div>
                    {filteredTxs.length === 0 ? (
                        <p className="p-8 text-center text-[11px] text-[#9CA3AF]">Eslesmemis transfer yok</p>
                    ) : (
                        <div className="max-h-[480px] overflow-y-auto divide-y divide-[#F3F4F6]">
                            {filteredTxs.map(t => (
                                <button key={t.id} onClick={() => toggleSet(setSelTx)(t.id)}
                                    className={`w-full text-left px-3 py-2 hover:bg-[#F9FAFB] ${selTx.has(t.id) ? 'bg-blue-50 ring-2 ring-blue-400' : ''}`}
                                    data-testid={`ledger-inbox-tx-${t.id}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-1.5">
                                            <input type="checkbox" checked={selTx.has(t.id)} readOnly className="w-3.5 h-3.5 accent-blue-600 pointer-events-none" />
                                            <span className="text-[11px] text-[#6B7280]">{t.transaction_date}</span>
                                        </div>
                                        <span className={`text-sm font-bold ${t.amount >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                            {t.amount >= 0 ? '+' : ''}{fmt(t.amount)}
                                        </span>
                                    </div>
                                    {t.communication && (
                                        <p className="text-[11px] text-[#374151] mt-1 break-words bg-blue-50/30 rounded px-2 py-1 border-l-2 border-blue-300">
                                            {t.communication}
                                        </p>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* SAG: Acik Faturalar */}
                <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden">
                    <div className="px-4 py-2.5 bg-[#FAFAFA] border-b border-[#F3F4F6]">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[12px] font-semibold text-[#374151]">Acik Faturalar ({filteredInvs.length})</span>
                            {selInv.size > 0 && (
                                <button onClick={() => setSelInv(new Set())} className="text-[10px] text-red-500 font-medium" data-testid="ledger-inbox-inv-clear">Temizle ({selInv.size})</button>
                            )}
                        </div>
                        {/* Tip filtresi (Inbox YON filtresi gibi) */}
                        <div className="flex gap-1 items-center mb-2 flex-wrap">
                            <span className="text-[9px] uppercase tracking-wider text-[#9CA3AF] font-semibold mr-1">Tip:</span>
                            {[
                                ['all', 'Tumu', null],
                                ['sales', 'Satis', 'emerald'],
                                ['purchase', 'Alis', 'blue'],
                                ['credit', 'Iade', 'rose'],
                            ].map(([k, l, c]) => {
                                const active = invType === k;
                                const activeCls = c === 'emerald' ? 'bg-emerald-600 text-white'
                                    : c === 'blue' ? 'bg-blue-600 text-white'
                                    : c === 'rose' ? 'bg-rose-600 text-white'
                                    : 'bg-[#111] text-white';
                                return (
                                    <button key={k} onClick={() => setInvType(k)}
                                        className={`px-2.5 py-0.5 rounded-md text-[10px] font-semibold ${active ? activeCls : 'bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]'}`}
                                        data-testid={`ledger-inbox-invtype-${k}`}>{l}</button>
                                );
                            })}
                        </div>
                        <div className="relative">
                            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                            <input value={invQ} onChange={e => setInvQ(e.target.value)}
                                placeholder="Fatura no, firma, tutar ara..."
                                className="w-full h-7 pl-6 pr-2 bg-white border border-[#E5E7EB] rounded text-[11px]"
                                data-testid="ledger-inbox-inv-search" />
                        </div>
                    </div>
                    {filteredInvs.length === 0 ? (
                        <p className="p-8 text-center text-[11px] text-[#9CA3AF]">Acik fatura yok</p>
                    ) : (
                        <div className="max-h-[480px] overflow-y-auto divide-y divide-[#F3F4F6]">
                            {filteredInvs.map(inv => {
                                const isCredit = (inv.type || '').includes('credit');
                                const isSel = selInv.has(inv.id);
                                const numMatch = inv._numMatch;
                                const amtExact = inv._amtExact;
                                let rowCls = 'hover:bg-[#F9FAFB]';
                                if (numMatch) rowCls = 'bg-emerald-50/60 hover:bg-emerald-100 border-l-4 border-emerald-500';
                                else if (amtExact && selTxObj) rowCls = 'bg-blue-50/40 hover:bg-blue-100 border-l-4 border-blue-400';
                                return (
                                    <label key={inv.id}
                                        className={`block px-3 py-2 cursor-pointer ${rowCls} ${isSel ? 'ring-2 ring-emerald-500' : ''}`}
                                        data-testid={`ledger-inbox-inv-${inv.id}`}>
                                        <div className="flex items-center gap-2">
                                            <input type="checkbox" checked={isSel} onChange={() => toggleSet(setSelInv)(inv.id)}
                                                className="w-3.5 h-3.5 accent-emerald-600" />
                                            <span className={`text-[12px] font-bold flex-1 truncate ${numMatch ? 'text-emerald-700' : 'text-[#111]'}`}>
                                                #{inv.invoice_number}
                                            </span>
                                            {numMatch && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-600 text-white font-bold">NO ✓</span>}
                                            {!numMatch && amtExact && selTxObj && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500 text-white font-bold">TAM TUTAR</span>}
                                            <TypeBadge type={inv.type} />
                                            <span className={`text-sm font-bold ${isCredit ? 'text-rose-600' : 'text-[#111]'}`}>
                                                {isCredit ? '-' : ''}{fmt(inv.remaining)}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-[#9CA3AF] pl-5">
                                            <span>{inv.invoice_date}</span>
                                            {inv.due_date && <span>vade: {inv.due_date}</span>}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Sticky eslestir bar */}
            {(selTx.size > 0 || selInv.size > 0) && (
                <div className="sticky bottom-3 z-30 bg-[#111] text-white rounded-2xl shadow-2xl" data-testid="ledger-inbox-match-bar">
                    <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-2 text-[11px]">
                            <span className="text-white/70">Transfer:</span>
                            <strong>{selTx.size} ad. · {fmt(sumSelTx)} EUR</strong>
                        </div>
                        <X size={12} className="text-white/40" />
                        <div className="flex items-center gap-2 text-[11px]">
                            <span className="text-white/70">Fatura:</span>
                            <strong>{selInv.size} ad. · {fmt(sumSelInv)} EUR</strong>
                        </div>
                        {selTx.size > 0 && selInv.size > 0 && (
                            <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${diff < 0.5 ? 'bg-emerald-500' : diff < Math.abs(sumSelInv) * 0.05 ? 'bg-amber-500' : 'bg-red-500'}`}>
                                Δ {fmt(diff)} EUR
                            </span>
                        )}
                        <button onClick={() => { setSelTx(new Set()); setSelInv(new Set()); setForceClose(false); }}
                            className="ml-auto h-8 px-3 bg-white/10 hover:bg-white/20 text-white text-[11px] font-semibold rounded-lg"
                            data-testid="ledger-inbox-clear-all">Temizle</button>
                        <button onClick={doMatch} disabled={matching || selTx.size === 0 || selInv.size === 0}
                            className="h-8 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-[12px] font-bold rounded-lg flex items-center gap-1.5 disabled:opacity-40"
                            data-testid="ledger-inbox-match-btn">
                            {matching ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                            {matching ? 'Eslestiriliyor...' : forceClose ? 'Tam Kapat & Eslestir' : 'Eslestir'}
                        </button>
                    </div>
                    {/* Farki kabul et — sadece MAKUL fark + dogru yon ise gosterilir.
                        Aksi halde "eksik secim" uyarisi cikar (saçma 16.745 EUR "komisyon" onerme!) */}
                    {selTx.size > 0 && selInv.size > 0 && diff > 0.5 && (
                        onlyCreditsSelected ? (
                            <div className="mx-3 mb-3 rounded-xl border-2 bg-orange-500/15 border-orange-400/60" data-testid="ledger-inbox-warn-credit-only">
                                <div className="flex items-start gap-3 px-3 py-2.5">
                                    <span className="text-orange-300 text-base leading-none mt-0.5">⚠</span>
                                    <div className="flex-1">
                                        <p className="text-[12px] font-bold text-orange-100">Sadece iade faturasi secili — eksik secim olabilir</p>
                                        <p className="text-[11px] text-orange-100/80 mt-0.5">
                                            Gelen tahsilatla yalniz iade(S-IADE) eslestirmek mantikli degildir. Muhtemelen ayni dekontta kapanan <strong>satis faturasini</strong> da secmelisin — o zaman iade faturayi azaltir, transfer net ile eslesir.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : isReasonableDiff ? (
                            <div className={`mx-3 mb-3 rounded-xl border-2 transition ${forceClose ? 'bg-amber-500/20 border-amber-400' : 'bg-white/5 border-white/10 hover:border-amber-400/50'}`}>
                                <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer">
                                    <input type="checkbox" checked={forceClose} onChange={e => setForceClose(e.target.checked)}
                                        className="w-4 h-4 accent-amber-400"
                                        data-testid="ledger-inbox-force-close" />
                                    <div className="flex-1">
                                        <p className="text-[12px] font-bold text-white flex items-center gap-1.5">
                                            💰 Farki kabul et — <span className="text-amber-300">{fmt(diff)} EUR</span> komisyon/kesinti
                                        </p>
                                        <p className="text-[10px] text-white/60 mt-0.5">
                                            Banka komisyonu, kur farki vs. icin — secilen fatura(lar) TAM kapatilir, cari hesap sifirlanir.
                                        </p>
                                    </div>
                                    {forceClose && (
                                        <span className="text-[10px] px-2 py-1 rounded bg-amber-400 text-[#111] font-bold whitespace-nowrap">
                                            ✓ AKTIF
                                        </span>
                                    )}
                                </label>
                            </div>
                        ) : (
                            <div className="mx-3 mb-3 rounded-xl border-2 bg-red-500/15 border-red-400/60" data-testid="ledger-inbox-warn-bigdiff">
                                <div className="flex items-start gap-3 px-3 py-2.5">
                                    <span className="text-red-300 text-base leading-none mt-0.5">⚠</span>
                                    <div className="flex-1">
                                        <p className="text-[12px] font-bold text-red-100">
                                            Fark cok buyuk (<span className="text-red-300">{fmt(diff)} EUR</span>) — banka komisyonu olamaz
                                        </p>
                                        <p className="text-[11px] text-red-100/80 mt-0.5">
                                            Bu fark, secili faturalarin %5'ini ({fmt(reasonableFeeLimit)} EUR ust limit) cok asiyor. Muhtemelen:
                                            <strong> seçim eksik </strong>(ayni dekontla kapanan diger faturalari/iadeleri de ekle) veya
                                            <strong> yanlis transfer </strong>secili. "Farki kabul et" sadece kucuk farklarda (komisyon/kur) gosterilir.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )
                    )}
                </div>
            )}
        </div>
    );
}

function TypeBadge({ type }) {
    const cfg = {
        sales: { l: 'S', cls: 'bg-emerald-100 text-emerald-700' },
        sales_credit: { l: 'S-IADE', cls: 'bg-rose-100 text-rose-700' },
        purchase: { l: 'A', cls: 'bg-blue-100 text-blue-700' },
        purchase_credit: { l: 'A-IADE', cls: 'bg-amber-100 text-amber-700' },
    }[type] || { l: '?', cls: 'bg-gray-100 text-gray-500' };
    return <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${cfg.cls}`}>{cfg.l}</span>;
}
